import React, { useCallback, useRef, useState } from "react";

import { classNames } from "@/util/class-names.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";

import { InputContainer } from "../TextField/TextField.jsx";
import type { TextFieldValidationProps } from "../util/use-validation-error.mjs";
import useValidationError from "../util/use-validation-error.mjs";

type InputFieldProps = {
  autoSize?: boolean;

  invert?: boolean;
} & React.ComponentProps<"textarea">;

type TextFieldProps = TextFieldValidationProps & InputFieldProps;

export const TextArea = ({
  autoSize,

  invert,

  className,

  error,
  errorInline,

  children,

  ...rest
}: TextFieldProps) => {
  const [shouldShowError, setShouldShowError] = useState(false);

  const onBlur = useCallback(() => {
    // If the field is required, show the error immediately otherwise only show if
    // the field has a value
    setShouldShowError(rest.required || !!rest.value);
  }, [setShouldShowError, rest.required, rest.value]);

  const sizerRef = useRef<HTMLDivElement>(null);

  const inputRef = useCallbackRef(
    (element: HTMLTextAreaElement) => {
      if (!element) return;

      function updateSize() {
        if (!sizerRef.current) return;
        sizerRef.current.innerText = element.value + " ";
      }

      if (autoSize) {
        element.addEventListener("input", updateSize);
        updateSize();
      }

      function preventPopup(e: Event) {
        setShouldShowError(true);

        e.preventDefault();
      }

      element.addEventListener("invalid", preventPopup, true);

      return () => {
        if (autoSize) {
          element.removeEventListener("input", updateSize);
        }

        element.removeEventListener("invalid", preventPopup, true);
      };
    },
    [autoSize],
  );

  const errorMessage = useValidationError(inputRef, { error, ...rest });

  return (
    <InputContainer
      {...classNames(className, invert && "invert")}
      data-validate={(shouldShowError || !!error) === true ? true : undefined}
    >
      {autoSize && <div ref={sizerRef} className="sizer" />}

      <textarea
        ref={inputRef}
        onBlur={onBlur}
        {...(shouldShowError && errorMessage ? { "aria-invalid": "true" } : {})}
        {...rest}
      />

      {children}

      <div role="alert" {...classNames(errorInline && "inline")}>
        {errorMessage}
      </div>
    </InputContainer>
  );
};
